import { Box, Container, Link, Typography } from '@mui/material';
import type { MultiProductBannerOfferType } from '@repo/supergraph/marketplace/multiProductBanner/types';
import type { MobileOrDesktop } from '@repo/utils/deviceType/types';
import { ProductCards } from '../ProductCards';

type EditorialBannerProps = {
  offers: MultiProductBannerOfferType[];
  deviceType?: MobileOrDesktop;
};

export const EditorialBanner = ({ offers, deviceType }: EditorialBannerProps) => {
  const isMobile = deviceType === 'mobile';

  return (
    <Box
      sx={{
        width: '100vw',
        position: 'relative',
        left: '50%',
        right: '50%',
        marginLeft: '-50vw',
        marginRight: '-50vw',
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'neutral.lighter',
        mb: 6,
        pt: isMobile ? 6 : undefined,
      }}
    >
      <Container maxWidth="xl" sx={{ paddingX: { xs: 3 } }}>
        <Box sx={{ maxWidth: { xs: '100%', md: '82%' }, margin: 'auto' }}>
          {!isMobile && (
            <Box
              sx={{
                display: 'flex',
                alignSelf: 'start',
                backgroundColor: 'greens.main',
                py: 1,
                px: 3,
                borderTopRightRadius: '8px',
                borderTopLeftRadius: '8px',
                mt: 6,
                width: 'fit-content',
                justifySelf: 'start',
              }}
            >
              <Typography variant="accent" sx={{ color: 'white', lineHeight: 1.5 }}>
                DID YOU KNOW?
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              py: { md: '32px', sm: '24px', xs: '24px' },
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              px: { xs: '24px', md: '40px' },
              backgroundColor: 'greens.lightest',
              gap: 3.5,
              mb: 6,
              borderRadius: '0 12px 12px 12px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                borderBottom: '4px solid',
                borderColor: 'greens.main',
                pb: 3,
                gap: 2,
              }}
            >
              <Typography variant="headline" sx={{ color: 'neutral.darkest', alignSelf: 'start' }}>
                {isMobile
                  ? 'Looking for a money market account? A high-yield savings could earn you more.'
                  : "Money market vs high-yield savings: What's the difference?"}
              </Typography>
              <Typography
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  border: '1px solid #81D6B8',
                  color: 'greens.darkest',
                  fontSize: '10px',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.65)',
                  display: 'flex',
                  height: 'fit-content',
                  lineHeight: 1,
                }}
              >
                AD
              </Typography>
            </Box>

            {!isMobile && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: { xs: '24px', sm: '32px' },
                    flexDirection: { xs: 'column', sm: 'row' },
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="smallHeadline" sx={{ color: 'neutral.darkest' }}>
                      Money market accounts
                    </Typography>
                    <Typography variant="text" sx={{ color: 'neutral.darkest' }}>
                      MMAs often come with checks and a debit card that you can use a few times a
                      month. You probably won’t get that from a high-yield savings account. MMAs are
                      handy if you want to get to your money fast.{' '}
                      <Typography variant="textBold">
                        They can be a good option if you need to park some cash, but also expect to
                        dip into it on occasion.
                      </Typography>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="smallHeadline" sx={{ color: 'neutral.darkest' }}>
                      High-yield savings
                    </Typography>
                    <Typography variant="text" sx={{ color: 'neutral.darkest' }}>
                      Some high-yield savings accounts come with ATM access, but you usually have to
                      transfer money to another account first before you can withdraw it.{' '}
                      <Typography variant="textBold">
                        High-yield savings accounts can be a good option for “set it and forget it”
                        savings goals, where all you want is for your cash to sit, earn interest and
                        grow.
                      </Typography>
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'greens.lighter',
                    width: '100%',
                    height: 0,
                  }}
                />

                <Typography
                  variant="smallHeadline"
                  sx={{ color: 'neutral.darkest', textAlign: 'center' }}
                >
                  Prefer a high-yield savings account? Check out our partner offers:
                </Typography>
              </>
            )}

            <ProductCards
              offers={offers.slice(0, 3)}
              impressionSection="banking_mma_editorial_card"
            />
            <Typography
              variant="textSmall"
              sx={{ color: 'neutral.darkest', textAlign: 'center', fontSize: '14px' }}
            >
              Want more options? See NerdWallet’s top choices for{' '}
              <Link href="/banking/best/high-yield-online-savings-accounts">
                High-Yield Savings Accounts
              </Link>
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
